// quoteApi.ts — the contract module for quote-to-draft-PO.
//
// Same convention as poApi.ts, poImportApi.ts and poScanApi.ts: every API-shape
// assumption for this flow lives here, read off
// api/src/procurement/quote-draft-service.js and quote-import-ai.js. Money is
// integer cents throughout; formatCents in poApi.ts is the one place it becomes
// dollars, and no page in this module divides by 100.
//
// ── WHY THESE TYPES ARE NOT poImportApi's ───────────────────────────────────
// The scan lane re-exports the import lane's staged shapes because it IS the
// import parser with a batch of one. This lane is not: a quote has no PO number,
// carries an expiry and a currency, splits a price into list and net, and keeps
// fees as their own rows. Reusing StagedExtraction here would mean a type that
// lies about half its fields. What IS shared comes from the review kit: the
// review entry shape and reviewKey (components/review/grounding), because the
// gate is literally the same code.

import { useApi } from '../apiHelpers';
import type {
  ImportLineType,
  ImportWarning,
} from './poImportApi';
import type { ReviewTarget, ConfirmReviewInput } from '../components/review/grounding';
import type { VendorResolution } from './poImportApi';
import { draftPageUrl, type ScanDraftSummary as QuoteDraftSummary } from './poScanApi';
import type { MaterialType, CommodityClass } from '../Receiving/types';

export type { ImportLineType, ImportWarning, VendorResolution } from './poImportApi';
export { reviewKey } from '../components/review/grounding';

/** How the quote reached us. Photos are pages of ONE quote, in page order. */
export type QuoteSource = 'quote_pdf' | 'quote_photo';

// ---------------------------------------------------------------------------
// Staged shapes
// ---------------------------------------------------------------------------

export interface QuoteLine {
  line_number: number;
  description: string | null;
  part_number: string | null;
  /**
   * Who MAKES the part, when the document said (migration 036). Carried onto the
   * mint card so a new catalog row is born with it, never onto the purchase order
   * itself, which has no notion of who made a thing.
   *
   * Null is the honest common case: a distributor's paperwork routinely names
   * only itself, and the prompt refuses to fall back to the letterhead.
   */
  manufacturer: string | null;
  quantity: number;
  /** The NET price per unit: what this order would actually pay. */
  unit_cost_cents: number | null;
  unit_price_text: string | null;
  unit_price_exact: boolean;
  /** True when the price was divided out of a line total rather than printed. */
  unit_price_derived: boolean;
  /** The PRE-discount price, only when the quote printed a separate higher one. */
  list_price_cents: number | null;
  /** The discount exactly as the vendor worded it ("22%", "less 10"). */
  discount_text: string | null;
  line_total_cents: number | null;
  line_type: ImportLineType;
  line_type_reason: string;
  /**
   * True for a row quoted BELOW the subtotal: shipping, bank charges, tariffs.
   * Carried onto the purchase order as a charge line so its total matches the
   * quote, and flagged for review because a fee is the row most likely to be a
   * duplicate of something billed separately.
   */
  is_fee: boolean;
  sku_guess: string | null;
  part_catalog_id: number | null;
  included: boolean;
  /** The reader's own "I could not read this". Becomes a mandatory review. */
  ai_not_sure: boolean;
}

export interface QuoteDiscount {
  line_number: number;
  description: string | null;
  list_price_cents: number | null;
  net_price_cents: number | null;
  discount_text: string | null;
  /** List minus net, times quantity. Null when no list price was printed. */
  saved_cents: number | null;
}

export interface QuoteVendor {
  name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface QuoteExtraction {
  quote_number: string | null;
  quote_date: string | null;
  expires_on: string | null;
  /** As PRINTED. Null when the page showed only a bare symbol. */
  currency: string | null;
  terms: string | null;
  ship_via: string | null;
  /** The company the quote was addressed to. Read so the direction can be checked. */
  quoted_to: string | null;
  ship_to_name: string | null;
  ship_to_attn: string | null;
  ship_to_address: string | null;
  vendor: QuoteVendor;
  /** Goods first, then fees. `is_fee` says which is which. */
  lines: QuoteLine[];
  goods_line_count: number;
  fee_line_count: number;
  discounts: QuoteDiscount[];
  document_discount_text: string | null;
  /** GOODS only, exactly as the document means "Subtotal". */
  subtotal_cents: number;
  printed_subtotal_cents: number | null;
  subtotal_source: 'document' | 'computed';
  fees_total_cents: number;
  printed_total_cents: number | null;
  computed_total_cents: number;
}

export interface QuoteConfidence {
  quote_number: number;
  quote_date: number;
  expires_on: number;
  currency: number;
  terms: number;
  ship_via: number;
  vendor: number;
  ship_to: number;
  lines: number;
  fees: number;
  subtotal_cents: number;
}

/** The import lane's reasons, plus the four only a quote can produce. */
export type QuoteReviewReason =
  | 'low_confidence'
  | 'unverified_text'
  | 'ungrounded_scan'
  | 'ai_remark'
  | 'model_not_sure'
  | 'fee_line'
  | 'currency_unstated'
  | 'total_mismatch'
  | 'subtotal_mismatch'
  | 'discount_inverted';

export interface QuoteReviewEntry {
  target: ReviewTarget;
  field: string | null;
  line_number: number | null;
  reason: QuoteReviewReason;
  message: string;
}

// ---------------------------------------------------------------------------
// The holding cell (migration 018)
// ---------------------------------------------------------------------------
//
// Read now, draft later. Both lanes share ONE table behind a `kind`
// discriminator, so the row shapes are the scan lane's and are imported rather
// than redefined. What differs is the route they are served from and what
// `po_number_guess` MEANS: on a quote row it holds the QUOTE number, because a
// quote carries no purchase-order number at all.

export type { ScanDraftSummary as QuoteDraftSummary, ScanDraftFile as QuoteDraftFile } from './poScanApi';
export { isDraftImage } from './poScanApi';

/** Where this lane's draft pages are served from. */
export const QUOTE_DRAFTS_PATH = '/procurement/quote-draft/drafts';

/**
 * An object URL for one page of a staged quote, or null.
 *
 * The same helper the scan lane uses, pointed at this lane's route. THE CALLER
 * OWNS THE REVOKE; leaking these pins every page ever viewed in memory for the
 * life of the tab.
 */
export const quoteDraftPageUrl = (draftId: number, page: number): Promise<string | null> =>
  draftPageUrl(draftId, page, QUOTE_DRAFTS_PATH);

export interface QuoteDraft {
  id: number;
  created_at: string;
  created_by_name: string | null;
  source: QuoteSource;
  vendor_guess: string | null;
  /** The QUOTE's number, despite the column name. See migration 018. */
  po_number_guess: string | null;
  /** The parse response VERBATIM — the review screen hydrates straight from it. */
  staged_doc: QuoteParseResponse;
  files: { page_number: number; mimetype: string; size_bytes: number }[];
}

export interface QuoteParseResponse {
  ok: true;
  instance: 'firefly' | 'swarm';
  source_filename: string | null;
  source: QuoteSource;
  extraction: QuoteExtraction;
  confidence: QuoteConfidence;
  warnings: ImportWarning[];
  vendor_resolution: VendorResolution;
  /** Every place a person must look at before this may become a draft. */
  review_required: QuoteReviewEntry[];
  /**
   * Highest numeric tail already taken per SKU stem. The SKU formula lives in
   * Parts/skuGrammar.ts; this is the one fact it cannot work out on its own.
   */
  sku_tail_max: Record<string, number>;
  /**
   * The holding-cell row this read was staged as, so the review survives
   * navigating away. Null when the stage failed, which is not fatal: the read
   * still came back, it just has no safety net.
   */
  draft_id?: number | null;
}

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

/**
 * A new catalog row this quote is asking to create. Both axes required.
 *
 * `mpn` is this lane's own field and is DELIBERATELY the reviewer's to keep or
 * clear. A quote's part number is the manufacturer's when the quote came from
 * the manufacturer, and the distributor's when it did not — and parts_catalog.mpn
 * is one of the two keys future matching indexes on, so a distributor's order
 * code saved here makes every later quote for the same component fail to match.
 */
export interface QuoteMintInput {
  /**
   * WHICH SORT OF THING, and therefore which code the server makes.
   *
   * Required on every mint. A `short` kind arrives with NO `sku` and the server
   * assigns `STEM-XXXX` inside the confirm transaction; a `structured` kind
   * arrives with the composed board SKU. Refusals: `mint_identity_required`,
   * `mint_invalid_kind_key`, `mint_name_too_long`; a database-level failure
   * comes back as `mint_failed` with `reason` and `line_number`.
   */
  kind_key: string;
  /** What people call it, <= 60 characters. Required for a short kind, which
   *  has no code that says anything; null for a board, which generates its own
   *  name from its segments. */
  name?: string | null;
  /** A BOARD ONLY. Absent means "assign one", which is what a short kind wants. */
  sku?: string;
  material_type: MaterialType;
  commodity_class: CommodityClass;
  description?: string | null;
  mpn?: string | null;
  /** Who MAKES it. The other half of the MPN — see migration 036. */
  manufacturer?: string | null;
}

export interface QuoteConfirmLineInput {
  description: string | null;
  quantity: number;
  line_type: ImportLineType;
  unit_cost_cents: number | null;
  part_catalog_id?: number | null;
  mint?: QuoteMintInput | null;
  included?: boolean;
}

export interface QuoteVendorCreate {
  name: string;
  type: string;
  /** Two uppercase letters, or absent. The manufacturer segment a board code on
   *  this same quote can carry; the vendor is created before the lines mint. */
  code?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  payment_terms?: string | null;
}

export interface QuoteConfirmInput {
  quote: {
    source_filename: string | null;
    source: QuoteSource;
    /** The staged extraction, echoed back. The notes are composed from it. */
    extraction: QuoteExtraction;
    vendor_id?: string | null;
    vendor_create?: QuoteVendorCreate | null;
    lines: QuoteConfirmLineInput[];
  };
  // Echoed for the same reason as the import lane: the server RECOMPUTES the
  // needs-review list from these three rather than trusting a client-supplied
  // one, so a payload cannot shorten its own homework.
  staged_extraction?: QuoteExtraction;
  staged_confidence?: QuoteConfidence;
  staged_warnings?: ImportWarning[];
  reviews?: ConfirmReviewInput[];
  /**
   * The holding-cell row this review came from. Cleared on success and left
   * alone on failure, so a rejected confirm keeps the quote exactly where it was.
   */
  draft_id?: number | null;
}

export interface QuoteConfirmResult {
  ok: true;
  purchase_order_id: string;
  po_number: string;
  /** Always 'draft'. A quote is evidence of nothing; submitting is a separate act. */
  status: string;
  vendor_id: string;
  vendor_name: string | null;
  minted: { line_number: number; part_catalog_id: number; sku: string; mpn: string | null }[];
  line_count: number;
}

export function useQuoteApi() {
  const api = useApi();

  return {
    /**
     * Read ONE quote. `files` is a single PDF, or up to eight photos of the
     * pages of one quote.
     *
     * Every page goes up under the SAME field name, in array order: the server
     * treats a repeated `file` field as the pages of one document, and page
     * order is upload order. Two PDFs in one request are refused rather than
     * batched — two quotes in one model context let a price from one land on
     * the other, undetectably.
     */
    parse: (files: File | File[]): Promise<QuoteParseResponse> => {
      const fd = new FormData();
      for (const file of Array.isArray(files) ? files : [files]) fd.append('file', file);
      return api.post('/procurement/quote-draft/parse', { body: fd });
    },

    /** Turn the reviewed quote into a DRAFT purchase order. */
    confirm: (body: QuoteConfirmInput): Promise<QuoteConfirmResult> =>
      api.post('/procurement/quote-draft/confirm', { body }),

    /** Everything read but not yet drafted, oldest first. Communal. */
    listDrafts: (): Promise<{ drafts: QuoteDraftSummary[] }> => api.get(QUOTE_DRAFTS_PATH),

    /** One staged quote, with the parse response to hydrate the screen from. */
    getDraft: (id: number): Promise<QuoteDraft> => api.get(`${QUOTE_DRAFTS_PATH}/${id}`),

    /** Throw a mis-read away. Its pages go with it. */
    discardDraft: (id: number): Promise<void> =>
      api.delete(`${QUOTE_DRAFTS_PATH}/${id}`, { skipJsonParse: true }),
  };
}
